// ObjectsManager.jsx - Main container for museum objects management
import React, { useState, useEffect, useCallback } from 'react';
import { Building, Plus, Settings, Package, ChevronDown, AlertCircle } from 'lucide-react';
import ObjectsList from './ObjectsList';
import ObjectEditor from './ObjectEditor';
import MuseumSettings from './MuseumSettings';
import { useMuseums } from '../hooks/useMuseums';
import { useMuseumObjects } from '../hooks/useMuseumObjects';
import { useGuides } from '../../contexts/GuidesContext';

const ObjectsManager = () => {
  const [activeView, setActiveView] = useState('objects'); // 'objects' or 'settings'
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingObject, setEditingObject] = useState(null);
  const [showCreateMuseum, setShowCreateMuseum] = useState(false);
  const [newMuseumName, setNewMuseumName] = useState('');

  // Museum state
  const {
    museums,
    currentMuseum,
    isLoading: museumsLoading,
    error: museumsError,
    fetchMuseums,
    fetchMuseum,
    createMuseum,
    updateMuseum,
    selectMuseum,
    addRoom,
    removeRoom,
    updateRooms,
    addCategory,
    removeCategory,
    updateCategories,
    linkGuide,
    unlinkGuide
  } = useMuseums();

  // Guides state (for linking guides to museums)
  const { fetchGuides, getCachedGuides } = useGuides();
  const [availableGuides, setAvailableGuides] = useState([]);

  // Objects state (depends on selected museum)
  const {
    objects,
    isLoading: objectsLoading,
    error: objectsError,
    filters,
    fetchObjects,
    createObject,
    updateObject,
    deleteObject,
    updateFilters,
    getNextNumber,
    checkNumberAvailable,
    initNewObject
  } = useMuseumObjects(currentMuseum?.id);

  // Load museums on mount
  useEffect(() => {
    fetchMuseums();
  }, [fetchMuseums]);

  // Load objects when museum changes
  useEffect(() => {
    if (currentMuseum?.id) {
      fetchObjects();
    }
  }, [currentMuseum?.id, fetchObjects]);

  // Load available guides for linking
  useEffect(() => {
    const loadGuides = async () => {
      try {
        const result = await fetchGuides('all');
        if (result?.guides) {
          setAvailableGuides(result.guides);
        }
      } catch (err) {
        console.error('Failed to load guides for linking:', err);
        // Try cached guides as fallback
        const cached = getCachedGuides('all');
        if (cached) {
          setAvailableGuides(cached);
        }
      }
    };
    loadGuides();
  }, [fetchGuides, getCachedGuides]);

  // Handle museum selection
  const handleMuseumChange = async (e) => {
    const museumId = e.target.value;
    if (museumId) {
      await fetchMuseum(museumId);
    } else {
      selectMuseum(null);
    }
  };

  // Handle creating a new museum
  const handleCreateMuseum = async () => {
    if (!newMuseumName.trim()) return;

    try {
      const result = await createMuseum({
        name: newMuseumName.trim(),
        createdBy: 'admin' // TODO: Use actual user ID
      });

      setNewMuseumName('');
      setShowCreateMuseum(false);

      // Select the newly created museum
      if (result?.id) {
        await fetchMuseum(result.id);
      }
    } catch (err) {
      console.error('Failed to create museum:', err);
    }
  };

  // Handle adding a new object
  const handleAddObject = async () => {
    const newObj = await initNewObject();
    setEditingObject(newObj);
    setIsEditorOpen(true);
  };

  // Handle editing an object
  const handleEditObject = (object) => {
    setEditingObject(object);
    setIsEditorOpen(true);
  };

  // Handle saving an object
  const handleSaveObject = async (objectData) => {
    if (editingObject?.id) {
      // Update existing
      await updateObject(editingObject.id, objectData);
    } else {
      // Create new
      await createObject({
        ...objectData,
        createdBy: 'admin' // TODO: Use actual user ID
      });
    }

    setIsEditorOpen(false);
    setEditingObject(null);
  };

  // Handle deleting an object
  const handleDeleteObject = async (objectId) => {
    await deleteObject(objectId);
  };

  // Handle filter changes
  const handleFilterChange = (newFilters) => {
    updateFilters(newFilters);
  };

  // Handle refresh
  const handleRefresh = () => {
    if (currentMuseum?.id) {
      fetchObjects();
    }
  };

  return (
    <div className="min-h-[600px] space-y-6">
      {/* Museum Selector */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Building className="w-5 h-5 text-blue-500" />
            <label className="font-medium text-gray-700">Museum:</label>
          </div>

          <div className="flex-1 min-w-[200px] max-w-md">
            <select
              value={currentMuseum?.id || ''}
              onChange={handleMuseumChange}
              disabled={museumsLoading}
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select a museum...</option>
              {museums.map(museum => (
                <option key={museum.id} value={museum.id}>
                  {museum.name}
                </option>
              ))}
            </select>
          </div>

          {/* Create Museum Button */}
          {!showCreateMuseum ? (
            <button
              onClick={() => setShowCreateMuseum(true)}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              New Museum
            </button>
          ) : (
            <div className="flex gap-2">
              <input
                type="text"
                value={newMuseumName}
                onChange={(e) => setNewMuseumName(e.target.value)}
                placeholder="Museum name"
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                autoFocus
                onKeyPress={(e) => e.key === 'Enter' && handleCreateMuseum()}
              />
              <button
                onClick={handleCreateMuseum}
                disabled={!newMuseumName.trim() || museumsLoading}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                Create
              </button>
              <button
                onClick={() => {
                  setShowCreateMuseum(false);
                  setNewMuseumName('');
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          )}
        </div>

        {/* Error Messages */}
        {(museumsError || objectsError) && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-600">
            <AlertCircle className="w-5 h-5" />
            {museumsError || objectsError}
          </div>
        )}
      </div>

      {/* Main Content */}
      {currentMuseum ? (
        <>
          {/* View Toggle */}
          <div className="flex gap-2">
            <button
              onClick={() => setActiveView('objects')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                activeView === 'objects'
                  ? 'bg-blue-500 text-white'
                  : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Package className="w-4 h-4" />
              Objects ({objects.length})
            </button>
            <button
              onClick={() => setActiveView('settings')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                activeView === 'settings'
                  ? 'bg-blue-500 text-white'
                  : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Settings className="w-4 h-4" />
              Settings
            </button>
          </div>

          {/* Content Area */}
          {activeView === 'objects' ? (
            <ObjectsList
              objects={objects}
              museum={currentMuseum}
              isLoading={objectsLoading}
              filters={filters}
              onFilterChange={handleFilterChange}
              onAddObject={handleAddObject}
              onEditObject={handleEditObject}
              onDeleteObject={handleDeleteObject}
              onRefresh={handleRefresh}
            />
          ) : (
            <MuseumSettings
              museum={currentMuseum}
              onUpdateMuseum={updateMuseum}
              onAddRoom={addRoom}
              onRemoveRoom={removeRoom}
              onUpdateRooms={updateRooms}
              onAddCategory={addCategory}
              onRemoveCategory={removeCategory}
              onUpdateCategories={updateCategories}
              onLinkGuide={linkGuide}
              onUnlinkGuide={unlinkGuide}
              availableGuides={availableGuides}
              isLoading={museumsLoading}
            />
          )}
        </>
      ) : (
        /* No Museum Selected */
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <Building className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">
            Welcome to Museum Objects Catalog
          </h3>
          <p className="text-gray-500 mb-6">
            Select an existing museum or create a new one to start managing objects
          </p>
          {museums.length === 0 && !museumsLoading && (
            <button
              onClick={() => setShowCreateMuseum(true)}
              className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors inline-flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Create Your First Museum
            </button>
          )}
        </div>
      )}

      {/* Object Editor Modal */}
      <ObjectEditor
        object={editingObject}
        museum={currentMuseum}
        isOpen={isEditorOpen}
        onClose={() => {
          setIsEditorOpen(false);
          setEditingObject(null);
        }}
        onSave={handleSaveObject}
        isLoading={objectsLoading}
        getNextNumber={getNextNumber}
        checkNumberAvailable={checkNumberAvailable}
      />
    </div>
  );
};

export default ObjectsManager;
